import axios from "axios";
import { DataTable } from "simple-datatables";
import { showAlert, showConfirm, handleRequestError } from "../../utils/alert";
import VMasker from "vanilla-masker";

export default () => ({
  isModalOpen: false,
  isSubmitting: false,
  editMode: false,
  editId: null,

  // Initialize as false. It will be updated via API inside loadSupportData().
  hasFinancialModule: false,

  paymentMethodsList: [],

  // Support data for Selects
  bankAccounts: [],
  categories: [],
  costCenters: [],

  form: {
    name: "",
    fee_percentage: "",
    fee_fixed: "",
    days_to_receive: 0,
    is_active: true,
    bank_account_id: "",
    category_id: "",
    cost_center_id: "",
  },

  dataTableInstance: null,

  async initPaymentMethods() {
    const token = localStorage.getItem("jwt_token");
    if (!token) return (window.location.href = "/client/login");

    window.axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

    // Always fetch support data to determine permissions and populate selects if allowed
    await this.loadSupportData();
    await this.loadTable();
  },

  async loadSupportData() {
    try {
      const res = await axios.get("/api/client/payment-methods/support-data");

      // The API dictates if the user has access to the financial module
      this.hasFinancialModule = res.data.has_financial_access;

      this.bankAccounts = res.data.bank_accounts;
      this.categories = res.data.categories;
      this.costCenters = res.data.cost_centers;
    } catch (e) {
      console.error("Erro ao carregar dados de suporte", e);
    }
  },

  applyMoneyMask(value) {
    if (!value) return "";
    const onlyNumbers = value.toString().replace(/\D/g, "");
    return VMasker.toMoney(onlyNumbers, {
      precision: 2,
      separator: ",",
      delimiter: ".",
      unit: "R$ ",
      zeroCents: false,
    });
  },

  applyPercentageMask(value) {
    if (!value) return "";
    const onlyNumbers = value.toString().replace(/\D/g, "");
    return VMasker.toMoney(onlyNumbers, {
      precision: 2,
      separator: ",",
      delimiter: ".",
      unit: "",
      zeroCents: false,
    });
  },

  handleFixedFeeInput(event) {
    this.form.fee_fixed = this.applyMoneyMask(event.target.value);
  },

  handlePercentageInput(event) {
    this.form.fee_percentage = this.applyPercentageMask(event.target.value);
  },

  unmaskValue(maskedValue) {
    if (!maskedValue) return 0;
    const cleanString = maskedValue
      .toString()
      .replace("R$ ", "")
      .replace(/\./g, "")
      .replace(",", ".");
    return parseFloat(cleanString);
  },

  formatDays(days) {
    if (days === 0) return "Na hora";
    if (days === 1) return "1 dia";
    return `${days} dias`;
  },

  async loadTable() {
    try {
      const response = await axios.get("/api/client/payment-methods");
      this.paymentMethodsList = response.data.data;

      if (this.dataTableInstance) this.dataTableInstance.destroy();

      const tbody = document.querySelector("#paymentMethodsTable tbody");
      tbody.innerHTML = this.paymentMethodsList
        .map((method) => {
          const statusBadge = method.is_active
            ? `<span class="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-bold text-green-700 ring-1 ring-inset ring-green-600/20">Ativo</span>`
            : `<span class="inline-flex items-center rounded-md bg-gray-50 px-2 py-1 text-xs font-bold text-gray-600 ring-1 ring-inset ring-gray-500/10">Inativo</span>`;

          let feeDisplay = [];
          if (parseFloat(method.fee_percentage) > 0)
            feeDisplay.push(`${method.fee_percentage.replace(".", ",")}%`);
          if (parseFloat(method.fee_fixed) > 0)
            feeDisplay.push(`R$ ${method.fee_fixed.replace(".", ",")}`);
          const finalFeeDisplay =
            feeDisplay.length > 0 ? feeDisplay.join(" + ") : "Sem taxa";

          // Dynamically show the bank account info only if the module is enabled and configured
          const bankInfo =
            this.hasFinancialModule &&
            method.bank_account_name !== "Não configurada"
              ? `<p class="text-[10px] uppercase font-bold text-slate-400 mt-1">${method.bank_account_name}</p>`
              : "";

          return `
            <tr class="hover:bg-slate-50 transition-colors">
              <td class="py-4 px-4 xl:px-6">
                <p class="font-bold text-slate-800">${method.name}</p>
                ${bankInfo}
              </td>
              <td class="py-4 px-4 xl:px-6 font-medium text-slate-700">${finalFeeDisplay}</td>
              <td class="py-4 px-4 xl:px-6 text-slate-600">${this.formatDays(method.days_to_receive)}</td>
              <td class="py-4 px-4 xl:px-6">${statusBadge}</td>
              <td class="text-left py-4 px-4 xl:px-6">
                <div class="flex justify-start gap-2">
                  <button onclick="window.dispatchEvent(new CustomEvent('edit-payment', {detail: ${method.id}}))" class="inline-flex items-center justify-center rounded-md bg-orange-500 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-orange-600">Editar</button>
                  <button onclick="window.dispatchEvent(new CustomEvent('delete-payment', {detail: ${method.id}}))" class="inline-flex items-center justify-center rounded-md bg-red-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-red-700">Excluir</button>
                </div>
              </td>
            </tr>
          `;
        })
        .join("");

      this.dataTableInstance = new DataTable("#paymentMethodsTable", {
        searchable: false,
        perPageSelect: false,
        labels: {
          info: "Mostrando de {start} até {end} de {rows} formas de pagamento",
          noRows: "Nenhuma forma de pagamento cadastrada",
        },
      });
    } catch (error) {
      console.error(error);
    }
  },

  openCreate() {
    this.editMode = false;
    this.form = {
      name: "",
      fee_percentage: "",
      fee_fixed: "",
      days_to_receive: 0,
      is_active: true,
      bank_account_id: "",
      category_id: "",
      cost_center_id: "",
    };
    this.isModalOpen = true;
  },

  openEdit(id) {
    this.editMode = true;
    this.editId = id;
    const method = this.paymentMethodsList.find((m) => m.id === id);

    this.form = {
      name: method.name,
      fee_percentage: this.applyPercentageMask(method.fee_percentage),
      fee_fixed: this.applyMoneyMask(method.fee_fixed),
      days_to_receive: method.days_to_receive,
      is_active: method.is_active,
      bank_account_id: method.bank_account_id || "",
      category_id: method.category_id || "",
      cost_center_id: method.cost_center_id || "",
    };
    this.isModalOpen = true;
  },

  async submitForm() {
    if (!this.form.name) {
      return showAlert("Preencha o nome da forma de pagamento.", "warning");
    }

    // Only require financial fields if the user has the module active
    if (this.hasFinancialModule) {
      if (!this.form.bank_account_id || !this.form.category_id) {
        return showAlert(
          "Preencha as configurações de integração financeira (*).",
          "warning",
        );
      }
    }

    this.isSubmitting = true;
    try {
      const apiPayload = {
        ...this.form,
        fee_percentage: this.unmaskValue(this.form.fee_percentage),
        fee_fixed: this.unmaskValue(this.form.fee_fixed),
      };

      if (this.editMode) {
        await axios.put(
          `/api/client/payment-methods/${this.editId}`,
          apiPayload,
        );
        showAlert("Forma de pagamento atualizada!", "success");
      } else {
        await axios.post("/api/client/payment-methods", apiPayload);
        showAlert("Forma de pagamento criada!", "success");
      }

      await this.loadTable();
      this.isModalOpen = false;
    } catch (error) {
      handleRequestError(error, "Erro ao guardar os dados.");
    } finally {
      this.isSubmitting = false;
    }
  },

  async deleteRecord(id) {
    const result = await showConfirm(
      "Excluir Forma de Pagamento",
      "Recomendamos inativar caso já possua movimentações históricas.",
    );
    if (result.isConfirmed) {
      try {
        await axios.delete(`/api/client/payment-methods/${id}`);
        await this.loadTable();
        showAlert("Removido com sucesso.", "success");
      } catch (error) {
        handleRequestError(error, "Erro ao excluir.");
      }
    }
  },
});
